/*
 * Manga Sekai Shop — "Pick 3, pay for 2" engine (SPEC §7, BUILDER).
 *
 * <mss-pick-tray> is rendered once by layout/theme.liquid (snippets/mss-pick-tray.liquid). PICK buttons are rendered
 * by snippets/mss-pick-button.liquid inside every product card (card-product, pick: true).
 *
 * Display only: the money logic is Shopify's automatic BXGY discount ("BUY 2 GET 1 FREE", buy 2 from
 * shop-by-character, get 1 free, repeats per 3). For every 3 counted units Shopify frees the cheapest box; the tray
 * mirrors that so its numbers match the cart.
 *
 * Ported from Kivora's kv-builder.js (compute(), the one-request add with drawer sections, focus/inert handling,
 * money formatting, edge states) and fixed:
 *  - picks live in localStorage `mss-picks-v1` (3-day TTL, a missing/invalid ts counts as expired) and follow the
 *    shopper across collection pages; every pick remembers the currency it was priced in, and is repriced (page card
 *    JSON, else /products/<handle>.js) when the shopper's currency changes — money stays hidden until all are repriced;
 *  - the cart counts toward the 3 (sections/mss-cart-state, rendered inline in the tray on page load and re-fetched
 *    after cart updates): eligible boxes AND gift cards from the B2G1 collection (Shopify counts gift cards as "buy"
 *    items but never frees them). No guessing from /cart.js: if the state is missing or out of step, it is re-fetched
 *    once, else the last good state is kept;
 *  - the idle tray only shows while a PICK button is on (or near) the screen, so it never covers a hero CTA or a buy box;
 *  - picks that can't be bought any more are flagged "No longer available — remove" (greyed in the tray, left out of
 *    the count and of "ADD ALL"): sold out (card JSON on the page, else /products/<handle>.js) or gone (the product
 *    was archived, unpublished or deleted → 404, or the variant no longer exists). Off-page picks are checked ONCE
 *    per browser session (sessionStorage `mss-pick-avail-v1`, 30-minute freshness), not on every page view;
 *  - "ADD ALL" checks any pick not yet verified this session first, sends the rest in ONE /cart/add.js request, and if
 *    Shopify still refuses, re-checks (bypassing the cache), flags the culprits and retries once with what is left;
 *  - a PICK tapped while "ADD ALL" is in flight is kept: only the quantities that were sent are taken off the tray;
 *  - the tray says what the picks ADD: saving = the cheapest box units freed by (cart + picks) minus what the cart
 *    already gets free on its own.
 * Public: window.MssPick { get(), add(item), remove(variantId), clear(), refreshCart(), state(), cartState() }, the
 * document events `mss:picks-changed` {detail: {count, cartEligible}} and `mss:cart-state` {detail: <mss-cart-state JSON>}.
 */
(() => {
  if (customElements.get('mss-pick-tray')) return;

  const SET = 3;
  const KEY = 'mss-picks-v1';
  const TTL = 3 * 24 * 60 * 60 * 1000;
  const SOURCE = 'mss-pick';
  const AVAIL_KEY = 'mss-pick-avail-v1';
  const AVAIL_TTL = 30 * 60 * 1000;
  const FLAGS = ['soldout', 'gone', 'limit'];

  /* ---------- helpers ---------- */

  function withDelimiters(cents, precision, thousands, decimal) {
    const value = Number(cents);
    if (!isFinite(value)) return '0';
    const parts = (value / 100).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
  }

  function formatWith(cents, format) {
    const fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    const token = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(token);
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(token, value);
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (whole, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : whole
    );
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function cleanItem(it, fallbackCur) {
    return {
      variantId: String(it.variantId),
      productId: String(it.productId || ''),
      title: String(it.title || ''),
      variantTitle: String(it.variantTitle || ''),
      image: it.image || '',
      url: it.url || '',
      price: Number(it.price) || 0,
      qty: Math.max(1, Math.floor(Number(it.qty)) || 1),
      cur: it.cur || fallbackCur || null,
      flag: FLAGS.indexOf(it.flag) !== -1 ? it.flag : null,
    };
  }

  function readStore() {
    let saved = null;
    try {
      const raw = window.localStorage.getItem(KEY);
      if (!raw) return { items: [] };
      saved = JSON.parse(raw);
    } catch (error) {
      saved = null;
    }
    const ts = saved ? Number(saved.ts) : NaN;
    const now = Date.now();
    if (!saved || !Array.isArray(saved.items) || !isFinite(ts) || ts > now + 60000 || now - ts > TTL) {
      try {
        window.localStorage.removeItem(KEY);
      } catch (error) {
        /* storage unavailable */
      }
      return { items: [] };
    }
    const items = saved.items
      .filter((it) => it && it.variantId && Number(it.qty) > 0)
      .map((it) => cleanItem(it, saved.cur));
    return { items };
  }

  function writeStore(items, currency) {
    try {
      if (!items.length) window.localStorage.removeItem(KEY);
      else window.localStorage.setItem(KEY, JSON.stringify({ ts: Date.now(), cur: currency || null, items }));
    } catch (error) {
      /* storage unavailable (private mode): picks just won't survive a reload */
    }
  }

  // Availability of products whose card is not on the page, per browser session: { "<url>.js": { ts, cur, gone, v: {
  // "<variantId>": [price, available 1|0] } } }. Only answers from Shopify are stored (a network error or a 429 is not).
  function readAvail() {
    try {
      const raw = window.sessionStorage.getItem(AVAIL_KEY);
      const map = raw ? JSON.parse(raw) : {};
      return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
    } catch (error) {
      return {};
    }
  }

  function writeAvail(map) {
    try {
      window.sessionStorage.setItem(AVAIL_KEY, JSON.stringify(map));
    } catch (error) {
      /* storage unavailable: the check simply runs again next page view */
    }
  }

  const variantCache = new WeakMap();
  function variantsFor(box) {
    if (!box) return [];
    if (variantCache.has(box)) return variantCache.get(box);
    let list = [];
    const node = box.querySelector('[data-mss-pick-variants]');
    if (node) {
      try {
        list = JSON.parse(node.textContent) || [];
      } catch (error) {
        list = [];
      }
    }
    variantCache.set(box, list);
    return list;
  }

  function productJsonUrl(url) {
    if (!url) return null;
    const path = String(url).split('#')[0].split('?')[0].replace(/\/$/, '');
    if (!/\/products\/[^/]+$/.test(path)) return null;
    return `${path}.js`;
  }

  /* ---------- the tray ---------- */

  class MssPickTray extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const cfgNode = this.querySelector('[data-mss-tray-config]');
      if (!cfgNode) return;
      try {
        this.cfg = JSON.parse(cfgNode.textContent);
      } catch (error) {
        console.error('[mss-pick] bad config', error);
        return;
      }
      if (this.cfg.enabled === false) return;
      this.ready = true;
      this.s = this.cfg.strings || {};
      this.maxPicks = Math.max(SET, Number(this.cfg.maxPicks) || 30);
      this.currency = (window.KvTheme && window.KvTheme.currency) || this.cfg.currency || null;
      this.busy = false;
      this.pricing = false;
      this.pickNear = false;
      this.cart = { loaded: false, items: 0, units: [], giftcards: 0, free: 0, applied: false };
      this.cartState = null;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this.panel = this.querySelector('[data-mss-tray-panel]');
      this.titleEl = this.querySelector('#MssTrayTitle');
      this.bar = this.querySelector('[data-mss-bar]');
      this.toggleBtn = this.querySelector('[data-mss-tray-action="toggle"]');
      this.statusEl = this.querySelector('[data-mss-status]');
      this.statusPanelEl = this.querySelector('[data-mss-status-panel]');
      this.moneyEl = this.querySelector('[data-mss-money]');
      this.miniTotalEl = this.querySelector('[data-mss-mini-total]');
      this.miniSaveEl = this.querySelector('[data-mss-mini-save]');
      this.miniCartEl = this.querySelector('[data-mss-mini-cart]');
      this.thumbsEl = this.querySelector('[data-mss-thumbs]');
      this.dots = Array.from(this.querySelectorAll('[data-mss-dots] > span'));
      this.listEl = this.querySelector('[data-mss-list]');
      this.emptyEl = this.querySelector('[data-mss-empty]');
      this.totalsEl = this.querySelector('[data-mss-totals]');
      this.subtotalEl = this.querySelector('[data-mss-subtotal]');
      this.freeRow = this.querySelector('[data-mss-free-row]');
      this.freeLabel = this.querySelector('[data-mss-free-label]');
      this.savingEl = this.querySelector('[data-mss-saving]');
      this.totalEl = this.querySelector('[data-mss-total]');
      this.cartNoteEl = this.querySelector('[data-mss-cartnote]');
      this.errorEl = this.querySelector('[data-mss-error]');
      this.ctaBtn = this.querySelector('[data-mss-cta]');
      this.ctaText = this.querySelector('[data-mss-cta-text]');
      this.liveEl = this.querySelector('[data-mss-live]');

      this.items = readStore().items;

      this.onDocClick = this.onDocClick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onStorage = this.onStorage.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onQueryChange = this.onQueryChange.bind(this);
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      window.addEventListener('storage', this.onStorage);
      window.addEventListener('pageshow', this.onPageShow);
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.onQueryChange);

      this.setupVisibility();

      // Cards re-rendered by Dawn (sort, pagination, search) or added later: keep their PICK state, the visibility
      // observer and the sold-out/price check in sync.
      this.observer = new MutationObserver((records) => {
        if (records.some((r) => Array.from(r.addedNodes).some((n) => n.nodeType === 1 && (n.matches('[data-mss-pick]') || n.querySelector('[data-mss-pick]'))))) {
          window.clearTimeout(this.syncTimer);
          this.syncTimer = window.setTimeout(() => {
            this.observePicks();
            this.checkPage();
            this.render({ quiet: true });
          }, 60);
        }
      });
      this.observer.observe(document.body, { childList: true, subtree: true });

      if ('ResizeObserver' in window && this.bar) {
        this.resizeObserver = new ResizeObserver(() => this.updateOffset());
        this.resizeObserver.observe(this.bar);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.source !== SOURCE) this.refreshCart();
        });
      }

      window.MssPick = {
        get: () => this.items.map((it) => Object.assign({}, it)),
        add: (item) => this.addItem(item),
        remove: (variantId) => this.removeOne(variantId),
        clear: () => this.clear(),
        refreshCart: () => this.refreshCart(),
        state: () => this.compute(),
        cartState: () => (this.cartState ? Object.assign({}, this.cartState) : null),
      };

      // Cart state rendered with the page (no request on load).
      const initial = this.querySelector('[data-mss-cart-initial] script[type="application/json"]');
      let initialState = null;
      if (initial) {
        try {
          initialState = JSON.parse(initial.textContent);
        } catch (error) {
          initialState = null;
        }
      }
      if (this.validState(initialState)) this.setCartState(initialState);

      this.checkPage();
      this.render({ quiet: true });
      this.verifyPicks();
      if (!this.cart.loaded) this.refreshCart();
    }

    disconnectedCallback() {
      if (!this.ready) return;
      document.removeEventListener('click', this.onDocClick);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.mobileQuery.removeEventListener) this.mobileQuery.removeEventListener('change', this.onQueryChange);
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.io) this.io.disconnect();
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      this.setBackgroundInert(false);
      document.documentElement.classList.remove('mss-tray-on');
      this.ready = false;
    }

    /* ---------- money ---------- */

    money(cents) {
      if (window.KvMoney && typeof window.KvMoney.format === 'function') {
        try {
          return window.KvMoney.format(cents);
        } catch (error) {
          /* fall through */
        }
      }
      return formatWith(cents, (window.KvTheme && window.KvTheme.moneyFormat) || this.cfg.moneyFormat);
    }

    /* ---------- state ---------- */

    active() {
      return this.items.filter((it) => !it.flag);
    }

    count() {
      return this.items.reduce((n, it) => n + it.qty, 0);
    }

    countForProduct(productId) {
      return this.items.reduce((n, it) => (it.productId === String(productId) && !it.flag ? n + it.qty : n), 0);
    }

    isStale(it) {
      return Boolean(this.currency) && it.cur !== this.currency;
    }

    save() {
      writeStore(this.items, this.currency);
    }

    // Prices and availability from the cards on this page (always the shopper's current currency).
    checkPage() {
      const found = new Map();
      document.querySelectorAll('[data-mss-pickbox]').forEach((box) => {
        variantsFor(box).forEach((v) => found.set(String(v.id), v));
      });
      this.onPage = new Set(found.keys());
      let changed = false;
      this.items.forEach((it) => {
        const v = found.get(it.variantId);
        if (!v) return;
        const price = Number(v.price) || 0;
        if (it.price !== price || it.cur !== this.currency) changed = true;
        it.price = price;
        if (this.currency) it.cur = this.currency;
        const flag = v.available === false ? 'soldout' : it.flag === 'limit' ? 'limit' : null;
        if (flag !== it.flag) changed = true;
        it.flag = flag;
      });
      if (changed) this.save();
      this.updateFlagNotice();
      return changed;
    }

    // Apply one cached/fetched product answer to a pick. Returns true when the pick changed.
    applyAvail(it, entry) {
      const before = `${it.flag}|${it.price}|${it.cur}`;
      if (entry.gone) {
        it.flag = 'gone';
      } else {
        const v = entry.v && entry.v[it.variantId];
        if (!v) {
          it.flag = 'gone';
        } else {
          it.price = Number(v[0]) || 0;
          if (entry.cur) it.cur = entry.cur;
          if (!v[1]) it.flag = 'soldout';
          else if (it.flag === 'soldout' || it.flag === 'gone') it.flag = null;
        }
      }
      return before !== `${it.flag}|${it.price}|${it.cur}`;
    }

    // Picks whose card is not on this page: availability + presentment price from /products/<handle>.js, at most once
    // per session per product (sessionStorage), refetched when older than 30 min, priced in another currency, or when
    // `force` (after Shopify refused an add). 404 = archived / unpublished / deleted → "gone".
    verifyPicks(options = {}) {
      const force = Boolean(options.force);
      const onPage = this.onPage || new Set();
      const wanted = this.items.filter((it) => force || !onPage.has(it.variantId) || this.isStale(it));
      const cache = readAvail();
      const now = Date.now();
      const currency = this.currency;
      let changed = false;
      const byUrl = new Map();
      wanted.forEach((it) => {
        const url = productJsonUrl(it.url);
        if (!url) return;
        const entry = cache[url];
        const fresh =
          entry && isFinite(entry.ts) && now - entry.ts < AVAIL_TTL && now >= entry.ts && (!currency || entry.gone || entry.cur === currency);
        if (!force && fresh) {
          if (this.applyAvail(it, entry)) changed = true;
          return;
        }
        if (!byUrl.has(url)) byUrl.set(url, []);
        byUrl.get(url).push(it);
      });

      const finish = () => {
        if (changed) this.save();
        this.updateFlagNotice();
        this.render({ quiet: true });
        return changed;
      };
      if (!byUrl.size) {
        this.pricing = false;
        return Promise.resolve(finish());
      }

      this.pricing = Array.from(byUrl.values()).some((list) => list.some((it) => this.isStale(it)));
      const seq = (this.priceSeq = (this.priceSeq || 0) + 1);
      this.render({ quiet: true });
      const jobs = Array.from(byUrl.entries()).map(([url, list]) =>
        fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then((res) => {
            if (res.status === 404) return { ts: Date.now(), cur: currency, gone: true };
            if (!res.ok) return null;
            return res.json().then((product) => {
              if (!product || !Array.isArray(product.variants)) return null;
              const v = {};
              product.variants.forEach((pv) => {
                v[String(pv.id)] = [Number(pv.price) || 0, pv.available === false ? 0 : 1];
              });
              return { ts: Date.now(), cur: currency, gone: false, v };
            });
          })
          .catch(() => null)
          .then((entry) => {
            if (!entry) return;
            cache[url] = entry;
            list.forEach((it) => {
              if (this.applyAvail(it, entry)) changed = true;
            });
          })
      );
      return Promise.all(jobs).then(() => {
        // Keep only the products still picked, so the session cache never grows.
        const keep = {};
        const latest = readAvail();
        this.items.forEach((it) => {
          const url = productJsonUrl(it.url);
          if (url && (cache[url] || latest[url])) keep[url] = cache[url] || latest[url];
        });
        writeAvail(keep);
        if (!this.ready) return false;
        if (seq === this.priceSeq) this.pricing = false;
        return finish();
      });
    }

    addItem(item) {
      if (!item || !item.variantId) return false;
      if (this.count() >= this.maxPicks) {
        this.showError(this.s.max);
        this.announce(this.s.max);
        return false;
      }
      const id = String(item.variantId);
      const existing = this.items.find((it) => it.variantId === id);
      if (existing) {
        existing.qty = existing.flag ? 1 : existing.qty + 1;
        existing.flag = null;
        existing.price = Number(item.price) || existing.price;
        existing.cur = this.currency || existing.cur;
      } else {
        this.items.push(
          cleanItem(
            {
              variantId: id,
              productId: item.productId,
              title: item.title,
              variantTitle: item.variantTitle,
              image: item.image,
              url: item.url,
              price: item.price,
              qty: 1,
            },
            this.currency
          )
        );
      }
      this.clearError(true);
      this.save();
      this.updateFlagNotice();
      const state = this.render({ bump: true });
      this.announce(`${fill(this.s.a11yAdded, { title: item.title || '' })} ${this.statusText(state)}`);
      return true;
    }

    removeOne(variantId) {
      const index = this.items.findIndex((it) => it.variantId === String(variantId));
      if (index < 0) return;
      const item = this.items[index];
      item.qty = item.flag ? 0 : item.qty - 1;
      if (item.qty <= 0) this.items.splice(index, 1);
      this.clearError(true);
      this.save();
      this.updateFlagNotice();
      const state = this.render({ bump: true });
      this.announce(`${fill(this.s.a11yRemoved, { title: item.title })} ${this.statusText(state)}`);
      return index;
    }

    clear() {
      this.items = [];
      this.save();
      this.clearError(true);
      this.render();
    }

    /* ---------- maths (mirrors Shopify BXGY: every 3 counted units free the cheapest box) ---------- */

    compute() {
      const cartUnits = (this.cart.units || []).map(Number).filter((p) => isFinite(p) && p >= 0);
      const picks = [];
      this.items.forEach((it, index) => {
        if (it.flag) return;
        for (let q = 0; q < it.qty; q += 1) picks.push({ price: it.price, index });
      });
      const E = cartUnits.length;
      const G = Math.max(0, Number(this.cart.giftcards) || 0);
      const N = picks.length;
      const C = E + G;
      const n = C + N;
      // Gift cards count as "buy" items but are never the free one.
      const freeAll = Math.min(Math.floor(n / SET), E + N);
      const freeCartOnly = Math.min(Math.floor(C / SET), E);
      const sumCheapest = (list, k) =>
        list
          .slice()
          .sort((a, b) => a - b)
          .slice(0, k)
          .reduce((s, p) => s + p, 0);
      const savingAll = sumCheapest(cartUnits.concat(picks.map((p) => p.price)), freeAll);
      const savingCart = sumCheapest(cartUnits, freeCartOnly);
      const subtotal = picks.reduce((s, p) => s + p.price, 0);
      const saving = Math.max(0, Math.min(subtotal, savingAll - savingCart));
      const freeAdded = Math.max(0, freeAll - freeCartOnly);

      // Which picks to tag FREE: the cheapest `freeAdded` pick units (later picks win ties).
      const freeByIndex = new Map();
      picks
        .map((p, i) => ({ p, i }))
        .sort((a, b) => a.p.price - b.p.price || b.i - a.i)
        .slice(0, freeAdded)
        .forEach(({ p }) => freeByIndex.set(p.index, (freeByIndex.get(p.index) || 0) + 1));

      const remainder = n % SET;
      return {
        E,
        G,
        C,
        N,
        n,
        flagged: this.items.filter((it) => it.flag).length,
        priced: !this.pricing && !this.items.some((it) => !it.flag && this.isStale(it)),
        freeAll,
        freeAdded,
        freeByIndex,
        subtotal,
        saving,
        total: subtotal - saving,
        remainder,
        need: n === 0 ? SET : remainder ? SET - remainder : 0,
        target: Math.max(SET, Math.ceil(n / SET) * SET),
      };
    }

    statusText(state = this.compute()) {
      if (!state.n) return this.s.idle || '';
      if (state.N === 0) {
        // Only cart boxes so far: say where the count comes from.
        if (state.remainder === 0) return fill(this.s.cartStatusFull, { n: state.C, k: state.freeAll });
        return fill(this.s.cartStatus, { n: state.C, k: state.need });
      }
      // [n] = boxes in the set (picks + eligible cart boxes), [p] = picks only, [box] = BOX / BOXES for [n],
      // [next] = free boxes once 2 more are added.
      const values = { n: state.n, p: state.N, m: state.target, k: state.freeAll, next: state.freeAll + 1,
        box: state.n === 1 ? (this.s.boxOne || 'BOX') : (this.s.boxMany || 'BOXES') };
      if (state.remainder === 1) return fill(state.freeAll > 0 ? (this.s.oneMore || this.s.one) : this.s.one, values);
      if (state.remainder === 2) return fill(this.s.two, values);
      return fill(this.s.full, values);
    }

    /* ---------- visibility: idle tray only near PICK buttons ---------- */

    hasPickButtons() {
      return Boolean(document.querySelector('[data-mss-pick]'));
    }

    setupVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.io = null;
        return;
      }
      this.near = new Set();
      this.watched = new WeakSet();
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.near.add(entry.target);
            else this.near.delete(entry.target);
          });
          const near = this.near.size > 0;
          if (near !== this.pickNear) {
            this.pickNear = near;
            this.render({ quiet: true });
          }
        },
        { rootMargin: '0px 0px 200px 0px' }
      );
      this.observePicks();
    }

    observePicks() {
      if (!this.io) return;
      document.querySelectorAll('[data-mss-pick]').forEach((btn) => {
        if (this.watched.has(btn)) return;
        this.watched.add(btn);
        this.io.observe(btn);
      });
      this.near.forEach((btn) => {
        if (!btn.isConnected) {
          this.near.delete(btn);
          this.io.unobserve(btn);
        }
      });
      const near = this.near.size > 0;
      if (near !== this.pickNear) this.pickNear = near;
    }

    shouldShow() {
      if (this.items.length) return true;
      if (this.io) return this.pickNear;
      return this.hasPickButtons();
    }

    /* ---------- rendering ---------- */

    render(options = {}) {
      const state = this.compute();
      const show = this.shouldShow();
      if (this.hidden === show) this.hidden = !show;
      document.documentElement.classList.toggle('mss-tray-on', show);
      if (!show && this.isOpen()) this.toggle(false, false);
      if (show && this.cartDirty) this.refreshCart();

      this.renderBar(state);
      this.renderPanel(state);
      this.syncCards();
      this.updateOffset();

      if (options.bump && this.bar) {
        this.bar.classList.remove('is-bump');
        void this.bar.offsetWidth;
        this.bar.classList.add('is-bump');
      }
      if (!options.quiet || options.bump) {
        document.dispatchEvent(new CustomEvent('mss:picks-changed', { detail: { count: state.N, cartEligible: state.C } }));
      }
      return state;
    }

    renderBar(state) {
      const status = this.statusText(state);
      if (this.statusEl) this.statusEl.textContent = status;
      if (this.statusPanelEl) this.statusPanelEl.textContent = status;

      const lit = state.n === 0 ? 0 : state.remainder === 0 ? SET : state.remainder;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-on', i < lit));
      this.classList.toggle('is-complete', state.n > 0 && state.remainder === 0);
      this.classList.toggle('has-picks', state.N > 0);

      if (this.moneyEl) this.moneyEl.hidden = state.N === 0;
      if (this.miniTotalEl) {
        // A pick that completes a set costs nothing extra: say FREE, never a bare $0.00 next to the add button.
        this.miniTotalEl.textContent = state.N
          ? (state.priced ? (state.total === 0 ? (this.s.free || 'FREE') : this.money(state.total)) : '…')
          : '';
      }
      if (this.miniSaveEl) {
        const showSave = state.priced && state.saving > 0;
        this.miniSaveEl.hidden = !showSave;
        this.miniSaveEl.textContent = showSave ? fill(this.s.save, { money: this.money(state.saving) }) : '';
      }
      if (this.miniCartEl) {
        const showCart = state.N > 0 && state.C > 0;
        this.miniCartEl.hidden = !showCart;
        this.miniCartEl.textContent = showCart ? fill(this.s.cartIncl, { e: state.C }) : '';
      }

      if (this.thumbsEl) {
        const frag = document.createDocumentFragment();
        const max = 5;
        this.items.slice(0, max).forEach((it) => {
          const t = el('span', it.flag ? 'mss-tray__thumb is-flagged' : 'mss-tray__thumb');
          if (it.image) {
            const img = document.createElement('img');
            img.src = it.image;
            img.alt = '';
            img.width = 36;
            img.height = 36;
            img.decoding = 'async';
            t.append(img);
          }
          if (it.qty > 1) t.append(el('span', 'mss-tray__thumb-qty', `×${it.qty}`));
          frag.append(t);
        });
        if (this.items.length > max) frag.append(el('span', 'mss-tray__thumb mss-tray__thumb--more', `+${this.items.length - max}`));
        this.thumbsEl.replaceChildren(frag);
      }

      if (this.ctaBtn) {
        this.ctaBtn.hidden = state.N === 0;
        if (!this.busy && this.ctaText) {
          // One box is a normal purchase, not an unfinished set: "ADD TO CART". 2+ picks short of a set: "ADD 2 TO CART".
          // A complete set (picks + eligible cart boxes): "ADD ALL 3 TO CART".
          // On a page with its own Add to cart (product page, featured product, open quick add) a bare "ADD TO CART"
          // here reads as that product's button, so the tray names the picks instead.
          const own = !!document.querySelector('product-info');
          const tpl = state.N === 1 ? ((own && this.s.ctaOneOwn) || this.s.ctaOne || this.s.cta)
            : state.remainder === 0 ? this.s.cta
            : ((own && this.s.ctaSomeOwn) || this.s.ctaSome || this.s.cta);
          this.ctaText.textContent = fill(tpl, { n: state.N });
        }
        this.ctaBtn.classList.toggle('is-ready', state.N > 0 && state.remainder === 0);
      }
    }

    renderPanel(state) {
      if (this.listEl) {
        const frag = document.createDocumentFragment();
        this.items.forEach((it, index) => {
          const li = el('li', it.flag ? 'mss-tray__item is-flagged' : 'mss-tray__item');
          const thumb = el('span', 'mss-tray__item-thumb');
          if (it.image) {
            const img = document.createElement('img');
            img.src = it.image;
            img.alt = '';
            img.width = 52;
            img.height = 52;
            img.decoding = 'async';
            thumb.append(img);
          }
          const info = el('span', 'mss-tray__item-info');
          const gone = it.flag === 'soldout' || it.flag === 'gone';
          // A gone product's page 404s: no link.
          const linked = it.url && it.flag !== 'gone';
          const name = linked ? el('a', 'mss-tray__item-title', it.title) : el('span', 'mss-tray__item-title', it.title);
          if (linked) name.href = it.url;
          info.append(name);
          if (it.variantTitle && it.variantTitle !== 'Default Title') info.append(el('span', 'mss-tray__item-variant', it.variantTitle));

          if (gone) {
            // "No longer available — remove": the whole row is greyed, one text button removes it.
            li.classList.add('is-gone');
            const note = el('span', 'mss-tray__gone', `${this.s.gone || 'No longer available'} — `);
            const drop = el('button', 'mss-tray__gone-remove', this.s.goneRemove || 'remove');
            drop.type = 'button';
            drop.dataset.mssTrayAction = 'remove';
            drop.dataset.variantId = it.variantId;
            drop.disabled = this.busy;
            drop.setAttribute('aria-label', `${this.s.removeAll || 'Remove'}: ${it.title} (${this.s.gone || 'No longer available'})`);
            note.append(drop);
            info.append(note);
            li.append(thumb, info);
            frag.append(li);
            return;
          }

          const price = el('span', 'mss-tray__item-price');
          if (it.flag) {
            price.append(el('span', 'mss-tray__flag', it.flag === 'limit' ? this.s.limitTag : this.s.soldOutTag));
          } else {
            const free = state.freeByIndex.get(index) || 0;
            const amount = state.priced && !this.isStale(it) ? this.money(it.price) : '…';
            price.append(el('span', 'mss-tray__item-amount', `${it.qty > 1 ? `${it.qty} × ` : ''}${amount}`));
            if (free > 0) price.append(el('span', 'mss-tray__free', free > 1 ? `${free} ${this.s.free || 'FREE'}` : this.s.free || 'FREE'));
          }

          const remove = el('button', 'mss-tray__remove');
          remove.type = 'button';
          remove.dataset.mssTrayAction = 'remove';
          remove.dataset.variantId = it.variantId;
          remove.disabled = this.busy;
          remove.setAttribute('aria-label', `${it.flag ? this.s.removeAll || 'Remove' : this.s.remove || 'Remove one'}: ${it.title}`);
          remove.innerHTML = it.flag
            ? '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 14 14"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
            : '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 14 14"><path d="M2 7h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

          li.append(thumb, info, price, remove);
          frag.append(li);
        });
        this.listEl.replaceChildren(frag);
      }
      if (this.emptyEl) this.emptyEl.hidden = this.items.length > 0;
      if (this.totalsEl) this.totalsEl.hidden = state.N === 0;
      if (this.subtotalEl) this.subtotalEl.textContent = state.priced ? this.money(state.subtotal) : '…';
      if (this.freeRow) this.freeRow.hidden = !state.priced || state.saving <= 0;
      if (this.freeLabel) this.freeLabel.textContent = `${this.s.freeRow || ''} (${state.freeAdded} ${this.s.free || 'FREE'})`;
      if (this.savingEl) this.savingEl.textContent = `−${this.money(state.saving)}`;
      if (this.totalEl) this.totalEl.textContent = state.priced ? this.money(state.total) : '…';

      if (this.cartNoteEl) {
        const show = state.C > 0;
        this.cartNoteEl.hidden = !show;
        this.cartNoteEl.textContent = show ? fill(state.C === 1 ? this.s.cartNoteOne : this.s.cartNote, { n: state.C }) : '';
      }
    }

    syncCards() {
      const picked = this.s.picked || 'PICKED ✓';
      const pick = this.s.pick || 'PICK';
      document.querySelectorAll('[data-mss-pick]').forEach((btn) => {
        const count = this.countForProduct(btn.dataset.productId);
        const on = count > 0;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-picked', on);
        const label = btn.querySelector('[data-mss-pick-label]');
        const qty = btn.querySelector('[data-mss-pick-qty]');
        const sr = btn.querySelector('[data-mss-pick-sr]');
        if (label) label.textContent = on ? picked : pick;
        if (qty) qty.textContent = count > 1 ? ` ×${count}` : '';
        if (sr) sr.textContent = count > 1 ? ` (${count}): ${btn.dataset.title || ''}` : `: ${btn.dataset.title || ''}`;
        const card = btn.closest('.card-wrapper, .mss-pick-card');
        if (card) card.classList.toggle('mss-is-picked', on);
      });
    }

    updateOffset() {
      if (!this.bar) return;
      const visible = !this.hidden;
      const height = visible ? Math.ceil(this.bar.getBoundingClientRect().height) + 16 : 0;
      document.documentElement.style.setProperty('--mss-tray-h', `${height}px`);
    }

    announce(message) {
      if (!this.liveEl || !message) return;
      window.clearTimeout(this.liveTimer);
      this.liveEl.textContent = '';
      this.liveTimer = window.setTimeout(() => {
        this.liveEl.textContent = message;
      }, 60);
    }

    // sticky = stays until the shopper changes the picks (used for sold-out notices and failed adds).
    showError(message, sticky = false) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.s.error;
      this.errorEl.hidden = false;
      this.errorSticky = sticky;
      window.clearTimeout(this.errorTimer);
      if (!sticky) this.errorTimer = window.setTimeout(() => this.clearError(), 8000);
    }

    clearError(force = false) {
      if (!this.errorEl || this.errorEl.hidden) return;
      if (this.errorSticky && !force) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.errorSticky = false;
    }

    updateFlagNotice() {
      const flagged = this.items.filter((it) => it.flag);
      if (flagged.length) {
        this.showError(fill(this.s.flagged, { titles: flagged.map((it) => it.title).join(', ') }), true);
      } else if (this.errorSticky && this.flagNoticeShown) {
        this.clearError(true);
      }
      this.flagNoticeShown = flagged.length > 0;
    }

    /* ---------- panel (mobile: sheet over a backdrop; desktop: pop-up above the bar) ---------- */

    isOpen() {
      return this.classList.contains('is-open');
    }

    setBackgroundInert(on) {
      document
        .querySelectorAll('#MainContent, .shopify-section-group-header-group, .shopify-section-group-footer-group')
        .forEach((node) => {
          node.inert = Boolean(on);
          if (on) node.setAttribute('aria-hidden', 'true');
          else node.removeAttribute('aria-hidden');
        });
    }

    toggle(force, moveFocus = true) {
      const open = typeof force === 'boolean' ? force : !this.isOpen();
      this.classList.toggle('is-open', open);
      if (this.panel) this.panel.hidden = !open;
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.setBackgroundInert(open && this.mobileQuery.matches);
      document.documentElement.classList.toggle('mss-tray-sheet-open', open && this.mobileQuery.matches);
      if (!moveFocus) return;
      if (open && this.titleEl) this.titleEl.focus({ preventScroll: true });
      else if (!open && this.toggleBtn) this.toggleBtn.focus();
    }

    focusable() {
      return Array.from(
        this.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((node) => node.getClientRects().length > 0 && !node.closest('[hidden]'));
    }

    trapTab(event) {
      const nodes = this.focusable();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      const outside = !this.contains(active) || active === this.titleEl;
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    onFocusOut(event) {
      const next = event.relatedTarget;
      if (!next || !this.isOpen() || this.contains(next)) return;
      this.toggle(false, false);
    }

    onQueryChange() {
      if (this.isOpen()) this.toggle(false, false);
      this.updateOffset();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen()) {
        event.stopPropagation();
        this.toggle(false);
        return;
      }
      if (event.key === 'Tab' && this.isOpen() && this.mobileQuery.matches) this.trapTab(event);
    }

    /* ---------- events ---------- */

    onDocClick(event) {
      const btn = event.target.closest('[data-mss-pick]');
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      const box = btn.closest('[data-mss-pickbox]');
      const variants = variantsFor(box);
      const select = box && box.querySelector('[data-mss-pick-variant]');
      let variant = null;
      if (select) variant = variants.find((v) => String(v.id) === String(select.value));
      if (!variant) variant = variants.find((v) => v.available) || null;
      if (!variant || !variant.available) {
        this.showError(this.s.unavailable);
        this.announce(this.s.unavailable);
        return;
      }
      const ok = this.addItem({
        variantId: variant.id,
        productId: btn.dataset.productId,
        title: btn.dataset.title,
        variantTitle: variants.length > 1 ? variant.title : '',
        image: variant.image || btn.dataset.image,
        url: btn.dataset.url,
        price: variant.price,
      });
      if (ok) {
        btn.classList.remove('is-plus');
        void btn.offsetWidth;
        btn.classList.add('is-plus');
      }
    }

    onClick(event) {
      const trigger = event.target.closest('[data-mss-tray-action]');
      if (!trigger || !this.contains(trigger)) return;
      switch (trigger.dataset.mssTrayAction) {
        case 'toggle':
          this.toggle();
          break;
        case 'close':
          this.toggle(false);
          break;
        case 'remove': {
          const index = this.removeOne(trigger.dataset.variantId);
          const buttons = this.listEl ? this.listEl.querySelectorAll('[data-mss-tray-action="remove"]') : [];
          const next = buttons[index] || buttons[index - 1];
          if (next) next.focus();
          else if (this.titleEl && this.isOpen()) this.titleEl.focus();
          break;
        }
        case 'add':
          this.addAll(trigger);
          break;
        default:
      }
    }

    onStorage(event) {
      if (event.key !== KEY) return;
      this.items = readStore().items;
      this.checkPage();
      this.render({ quiet: true });
      this.verifyPicks();
    }

    onPageShow(event) {
      if (!event.persisted) return;
      this.items = readStore().items;
      this.checkPage();
      this.render({ quiet: true });
      this.verifyPicks();
      this.refreshCart();
    }

    /* ---------- cart ---------- */

    routes() {
      const r = window.routes || {};
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || this.cfg.rootUrl || '/';
      return {
        add: `${r.cart_add_url || this.cfg.cartAddUrl || '/cart/add'}.js`,
        cart: r.cart_url || this.cfg.cartUrl || '/cart',
        root: root.endsWith('/') ? root : `${root}/`,
      };
    }

    parseCartState(html) {
      if (!html) return null;
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.getElementById('mss-cart-state');
        return node ? JSON.parse(node.textContent) : null;
      } catch (error) {
        return null;
      }
    }

    validState(state) {
      return Boolean(
        state &&
          Array.isArray(state.eligible_unit_prices) &&
          typeof state.items === 'number' &&
          state.eligible_unit_prices.length === Math.min(60, Number(state.eligible))
      );
    }

    setCartState(state) {
      this.cartState = state;
      this.cart = {
        loaded: true,
        items: Number(state.items) || 0,
        units: state.eligible_unit_prices.map(Number),
        giftcards: Math.max(0, Number(state.giftcard_units) || 0),
        free: Math.max(0, Number(state.free_units) || 0),
        applied: Boolean(state.applied),
      };
      if (state.currency && this.currency && state.currency !== this.currency) {
        this.currency = state.currency;
        this.checkPage();
        this.verifyPicks();
      }
      document.dispatchEvent(new CustomEvent('mss:cart-state', { detail: Object.assign({}, state) }));
    }

    // sections/mss-cart-state (CART) is the only source: it knows which lines are boxes. Trusted when its item count
    // matches /cart.js; otherwise (a race right after a cart change) it is re-fetched once, then the last good state
    // is kept. /cart.js lines are never counted as boxes on their own.
    applyCart(cart, state, attempt = 0) {
      const itemCount = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
      if (this.validState(state) && (itemCount === null || Number(state.items) === itemCount)) {
        this.setCartState(state);
      } else if (attempt === 0) {
        this.fetchCart(1, 300);
        return;
      }
      if (cart && cart.currency && this.currency && cart.currency !== this.currency) {
        this.currency = cart.currency;
        this.checkPage();
        this.verifyPicks();
      }
      this.render({ quiet: true });
    }

    fetchCart(attempt = 0, delay = 120) {
      window.clearTimeout(this.cartTimer);
      this.cartTimer = window.setTimeout(() => {
        const r = this.routes();
        const seq = (this.cartSeq = (this.cartSeq || 0) + 1);
        const cartReq = fetch(`${r.cart}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);
        const stateReq = fetch(`${r.root}?sections=mss-cart-state`, { credentials: 'same-origin' })
          .then((res) => (res.ok ? res.json() : null))
          .then((json) => this.parseCartState(json && json['mss-cart-state']))
          .catch(() => null);
        Promise.all([cartReq, stateReq]).then(([cart, state]) => {
          if (!this.ready || seq !== this.cartSeq) return;
          this.applyCart(cart, state, attempt);
        });
      }, delay);
    }

    // Skipped while the tray is hidden and empty (nothing shows the count); done when the tray next shows.
    refreshCart() {
      if (!this.items.length && !this.shouldShow()) {
        this.cartDirty = true;
        return;
      }
      this.cartDirty = false;
      this.fetchCart(0, 120);
    }

    setBusy(busy) {
      this.busy = busy;
      this.classList.toggle('is-busy', busy);
      // The sent picks are already on their way to the cart: removing one mid-request would mislead. PICK stays live
      // (taps are queued in the tray and stay there after the add).
      this.querySelectorAll('[data-mss-tray-action="remove"]').forEach((btn) => {
        btn.disabled = busy;
      });
      if (!this.ctaBtn) return;
      this.ctaBtn.classList.toggle('is-loading', busy);
      this.ctaBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.ctaBtn.disabled = busy;
      if (busy && this.ctaText) this.ctaText.textContent = this.s.adding || '…';
    }

    createLinesEvent(items) {
      try {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent || typeof CartLinesUpdateEvent.createPromise !== 'function') return null;
        const deferred = CartLinesUpdateEvent.createPromise();
        // A failed add rejects this promise. Shopify's event wraps it in a promise of its own (event.promise): catch
        // both so a refused add never reaches the page's error trackers as an uncaught rejection.
        const silence = (promise) => {
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        };
        silence(deferred && deferred.promise);
        const event = new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        });
        silence(event.promise);
        this.dispatchEvent(event);
        return deferred;
      } catch (error) {
        return null;
      }
    }

    settleLinesEvent(deferred, error) {
      if (!deferred) return;
      try {
        if (error) {
          deferred.reject(error);
          return;
        }
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        fetch(`${this.routes().cart}.json`)
          .then((response) => response.json())
          .then((cart) => {
            if (!cart || !cart.currency) throw new Error('Missing currency in cart response');
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      } catch (e) {
        /* analytics only */
      }
    }

    // After a refused "ADD ALL": find the picks Shopify refused (sold out, gone, or more than the stock) and flag them,
    // so the rest can be added. Product JSON (cache bypassed) gives availability; the error text names the product
    // for stock limits. Resolves true when it flagged something new.
    diagnoseFailure(message) {
      const before = this.items.filter((it) => it.flag).length;
      return this.verifyPicks({ force: true }).then(() => {
        let flagged = this.items.filter((it) => it.flag).length;
        if (flagged === before && message) {
          const text = String(message).toLowerCase();
          this.items.forEach((it) => {
            if (!it.flag && it.title && text.indexOf(it.title.toLowerCase()) !== -1) it.flag = 'limit';
          });
          flagged = this.items.filter((it) => it.flag).length;
        }
        this.save();
        this.updateFlagNotice();
        this.render({ quiet: true });
        return flagged > before;
      });
    }

    // ADD ALL: verify picks not yet checked this session (no request when all are known), then ONE /cart/add.js.
    addAll(trigger) {
      if (this.busy || !this.active().length) return;
      this.clearError(true);
      this.setBusy(true);
      this.verifyPicks()
        .catch(() => false)
        .then(() => {
          if (!this.ready) return;
          this.sendAll(trigger, 0);
        });
    }

    sendAll(trigger, attempt) {
      const picks = this.active();
      if (!picks.length) {
        // Everything picked turned out to be unavailable: the flagged rows and the sticky notice say so.
        this.setBusy(false);
        this.render({ quiet: true });
        this.updateFlagNotice();
        if (this.errorEl && !this.errorEl.hidden) this.announce(this.errorEl.textContent);
        return;
      }
      const items = picks.map((it) => ({ id: Number(it.variantId), quantity: it.qty }));
      // What this request carries, so picks tapped while it is in flight survive it.
      const sent = new Map(picks.map((it) => [it.variantId, it.qty]));
      const r = this.routes();
      const onCartPage = window.location.pathname.replace(/\/$/, '') === r.cart.replace(/\/$/, '');
      const drawer = document.querySelector('cart-drawer');
      const canRenderDrawer =
        !onCartPage && drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function';
      const body = { items };
      const sections = canRenderDrawer ? drawer.getSectionsToRender().map((s) => s.id) : [];
      if (sections.indexOf('mss-cart-state') === -1) sections.push('mss-cart-state');
      body.sections = sections.slice(0, 5);
      body.sections_url = window.location.pathname;
      if (canRenderDrawer && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger || this.ctaBtn);

      this.setBusy(true);
      const linesEvent = this.createLinesEvent(items);

      fetch(r.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || (data && data.status)) {
            const message = (data && (data.description || data.message)) || this.s.error;
            this.settleLinesEvent(linesEvent, new Error(message));
            // Find the pick(s) Shopify refused; when found, retry ONCE with the rest so one dead pick never blocks
            // the others. Picks are not touched until the retry has an answer.
            this.diagnoseFailure(message).then((found) => {
              if (!this.ready) return;
              if (found && attempt === 0 && this.active().length) {
                this.sendAll(trigger, 1);
                return;
              }
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: SOURCE,
                  productVariantId: items[0] && items[0].id,
                  errors: data && (data.errors || data.description),
                  message: data && data.message,
                });
              }
              this.setBusy(false);
              this.render({ quiet: true });
              if (found) {
                this.updateFlagNotice();
              } else {
                this.showError(message, true);
              }
              this.announce(this.errorEl && !this.errorEl.hidden ? this.errorEl.textContent : message);
            });
            return;
          }

          this.settleLinesEvent(linesEvent);
          // Take off only what was sent: a PICK tapped during the request (a new box, or one more of a sent box) stays.
          this.items = this.items.filter((it) => {
            const qty = sent.get(it.variantId);
            if (!qty || it.flag) return true;
            it.qty -= qty;
            return it.qty > 0;
          });
          this.save();
          this.setBusy(false);
          this.updateFlagNotice();
          if (this.isOpen()) this.toggle(false, false);
          this.render();
          this.announce(this.s.added);

          if (onCartPage) {
            window.location.reload();
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: data });
          }
          if (canRenderDrawer && data && data.sections) {
            try {
              drawer.classList.remove('is-empty');
              drawer.renderContents(data);
            } catch (error) {
              console.error('[mss-pick] drawer render failed', error);
              window.location = r.cart;
              return;
            }
          } else {
            window.location = r.cart;
            return;
          }
          const state = data && data.sections ? this.parseCartState(data.sections['mss-cart-state']) : null;
          if (this.validState(state)) {
            this.setCartState(state);
            this.render({ quiet: true });
          } else {
            this.refreshCart();
          }
        })
        .catch((error) => {
          console.error('[mss-pick]', error);
          this.settleLinesEvent(linesEvent, error);
          this.setBusy(false);
          this.render({ quiet: true });
          this.showError(this.s.error);
          this.announce(this.s.error);
        });
    }
  }

  customElements.define('mss-pick-tray', MssPickTray);
})();
